import { GptWrapper } from "./GptWrapper";
import type { AdEnginePlugin } from "./AdEnginePlugin";
import { runHook } from "./runHook";
import { AdNodeManager } from "./AdNodeManager";
import { PrivacyManager } from "./PrivacyManager";
import type { PageTargeting } from "../config/page";
import { getApp } from "../helpers/getApp";
import { type FandomCmp, PerformanceMarkName } from "common-types";
import { AdEngine, type AdEngineConfig } from "./AdEngine";
import { MonetizationTierEngine } from "./MonetizationTierEngine";

async function initSlotConfig(app?: string) {
  if (window.fandomAdsSlotsConfig) {
    return window.fandomAdsSlotsConfig;
  } else {
    const { loadConfig } = await import("./handleDynamicConfig");

    const config = await loadConfig(app);
    window.fandomAdsSlotsConfig = config;
    return config;
  }
}

/** Wait for the host app and the configurations to load before continuing */
async function waitForHostApp() {
  return new Promise((resolve) => {
    window.fandomHost = window.fandomHost ?? {};
    window.fandomHost.cmd = window.fandomHost.cmd ?? [];
    window.fandomHost.cmd.push(resolve);
  });
}

export async function initAdEngine(
  config: AdEngineConfig,
  plugins: AdEnginePlugin[],
) {
  // A lot of parts of the core AdEngine will depend on the monetization tier
  const monetizationTierEngine = new MonetizationTierEngine(config);

  // FUTURE: The monetization tier should be incorporated into config and
  // this should be reworked, so not not the app, but also the current
  // monetization tier affects the slot and partner config (ie: ids).
  // Consider also separating slot config from partners' config.
  const initialSlotsConfig = await initSlotConfig(getApp());
  // If monetization tier is enabled, we need to ensure that the initial slots config
  // is compatible with the monetization tier.
  // if (monetizationTierEngine.isEnabled()) {
  //    initialSlotsConfig = monetizationTierEngine.filterSlotsForCurrentTier(initialSlotsConfig);
  // ]
  const initialPageTargeting: PageTargeting = {};

  runHook(plugins, "onInitPageTargeting", initialPageTargeting);
  runHook(plugins, "onInitSlotConfigMap", initialSlotsConfig);

  // Create the gptWrapper for the command queue and ensure other manager can get access to gpt
  const gptWrapper = new GptWrapper(initialPageTargeting, {
    onBeforeAdsRequest: (adNodes) => {
      runHook(plugins, "onBeforeAdsRequest", adNodes);
    },
  });

  /**
   * Initialize Privacy Manager
   */
  const privacyManager = new PrivacyManager(gptWrapper, {
    onConsentUpdated: (consentData: FandomCmp) => {
      runHook(plugins, "onConsentUpdated", consentData);
    },
  });
  await Promise.all([privacyManager.waitForConsents(), waitForHostApp()]);

  /**
   * Initialize Gpt
   *
   * At this point, initial page targeting has been applied to the Gpt object and is no longer
   * valid way to handle page targeting. From here on `gptWrapper.getAllTargeting()` should be used
   * instead to get all the latest targeting.
   */
  await gptWrapper.enableServices();
  runHook(plugins, "onAfterGptWrapperInit", gptWrapper);
  performance.mark(PerformanceMarkName.AE_AFTER_GPT_WRAPPER_INIT);

  /**
   * Initialize Ad Node Manager
   */
  const adNodeManager = new AdNodeManager(initialSlotsConfig, gptWrapper);
  adNodeManager.addAllStaticSlots();

  // Register all slots with gpt
  runHook(
    plugins,
    "onAfterAdNodeManagerInit",
    adNodeManager,
    initialSlotsConfig.baseSlots,
  );

  await privacyManager.waitForAdsAllowed();

  const adEngine = new AdEngine(
    config,
    plugins,
    gptWrapper,
    adNodeManager,
    monetizationTierEngine,
  );

  /**
   * Ad Engine Initialization is complete, emit the hook
   */
  runHook(plugins, "onAdEngineInit", adEngine);
  performance.mark(PerformanceMarkName.AE_AD_ENGINE_INIT);

  return adEngine;
}
